
import React, { useState } from 'react';
import { useAppStore } from '@/store';
import { formatRelativeTime, truncateText } from '@/lib/utils';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Message } from '@/types';
import { useIsMobile } from '@/hooks/use-mobile';
import { Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

interface ChatHistorySidebarProps {
  onSelectHistory: (messages: Message[]) => void;
  onClose: () => void;
}

const ChatHistorySidebar: React.FC<ChatHistorySidebarProps> = ({ 
  onSelectHistory, 
  onClose 
}) => {
  const { chats, models } = useAppStore();
  const isMobile = useIsMobile();
  const [searchTerm, setSearchTerm] = useState('');
  
  // Filter chats by search term
  const filteredChats = React.useMemo(() => {
    if (!searchTerm.trim()) return chats;
    
    return chats.filter(chat => 
      chat.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      chat.messages.some(msg => msg.content.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [chats, searchTerm]);
  
  // Sort chats by updated time (most recent first)
  const sortedChats = React.useMemo(() => {
    return [...filteredChats].sort((a, b) => 
      new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    );
  }, [filteredChats]);

  return (
    <div className="h-full flex flex-col">
      <div className="p-3 border-b">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-semibold">Chat History</h2>
          {isMobile && (
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={onClose} 
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
        
        <p className="text-sm text-muted-foreground">
          Click on a conversation to add its messages to your current chat
        </p>
        
        <div className="relative mt-2">
          <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search conversations..."
            className="pl-8"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>
      
      <ScrollArea className="flex-1">
        {sortedChats.length === 0 ? (
          <div className="p-4 text-center text-muted-foreground">
            {searchTerm ? "No matching conversations found" : "No conversations yet"}
          </div>
        ) : (
          <div className="divide-y">
            {sortedChats.map(chat => {
              const model = models.find(m => m.id === chat.modelId);
              const lastMessage = chat.messages[chat.messages.length - 1];
              
              return (
                <div 
                  key={chat.id}
                  className="p-3 hover:bg-accent/50 active:bg-accent/70 cursor-pointer transition-colors"
                  onClick={() => {
                    onSelectHistory(chat.messages);
                    onClose();
                  }}
                >
                  <div className="font-medium">{truncateText(chat.title, isMobile ? 24 : 30)}</div>
                  <div className="text-xs text-muted-foreground mt-1 flex justify-between">
                    <span className="truncate max-w-[100px]">{model?.name || 'AI Assistant'}</span>
                    <span>{formatRelativeTime(chat.updatedAt)}</span>
                  </div>
                  {lastMessage && (
                    <div className="text-sm mt-1 text-muted-foreground line-clamp-1">
                      {truncateText(lastMessage.content, isMobile ? 36 : 40)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </ScrollArea>
    </div>
  );
};

export default ChatHistorySidebar;
